import { FormHelperText } from "@mui/material"
import Box from "@mui/material/Box"
import InputLabel from "@mui/material/InputLabel"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useEffect, useId, useMemo, useRef, useState } from "react"
import type { Control, FieldValues } from "react-hook-form"
import { Controller } from "react-hook-form"
import _PhoneInput, { CountryData } from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"

import type { TextInputProps } from "@components/text_input/sub_components/primary_input"
import { INPUT_LABEL_BLUR, NAV_PANEL_BG, PRIMARY } from "@constants/colors"
import useTranslate from "@hooks/use_translate"

import styles from "./styles"

export type PhoneData = {
  value: string
  dialCode: string
  countryCode: string
}

export default function PhoneInput<T extends FieldValues, K = unknown>(
  props: TextInputProps<T, K>
) {
  const [isFocus, setIsFocus] = useState(false)
  const componentId = useId()
  const { translate } = useTranslate()

  const TitleRightMemo = useMemo(() => {
    if (!props?.TitleRight) {
      return undefined
    }

    return props.TitleRight
  }, [props])

  function onFocus(event: React.FocusEvent<HTMLInputElement>) {
    setIsFocus(true)
    props?.onFocus && props?.onFocus(event)
  }

  function onBlur(event: React.FocusEvent<HTMLInputElement>) {
    setIsFocus(false)
    props?.onBlur && props?.onBlur(event)
  }

  function handleOnChange(
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  ) {
    return (value: string, countryData: Partial<CountryData>) => {
      const _value = {
        value,
        dialCode: countryData?.dialCode,
        countryCode: countryData?.countryCode
      }
      //@ts-expect-error : there is no issue with this line of code
      onChange({ target: { value: _value as unknown } })
    }
  }

  const inputRef = useRef<HTMLInputElement>(null)
  const [dropdownStyle, setDropdownStyle] = useState<{ width: string }>()

  useEffect(() => {
    if (inputRef.current) {
      const inputWidth = inputRef.current.offsetWidth
      // Set the width of the dropdown style dynamically
      setDropdownStyle({ width: `${inputWidth}px` })
    }
  }, [inputRef.current])

  return (
    <Box sx={styles.container}>
      <Controller<FieldValues>
        name={props.name}
        control={props.control as unknown as Control<FieldValues>}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Stack
              justifyContent={"space-between"}
              direction={"row"}
              spacing={2}
            >
              <InputLabel
                sx={{
                  ...styles.labelContainer,
                  color: isFocus ? PRIMARY : INPUT_LABEL_BLUR
                }}
                error={!!error}
                htmlFor={componentId}
                required={props?.required}
              >
                <Typography fontSize={props?.size === "small" ? 14 : undefined}>
                  {props.label}
                </Typography>
              </InputLabel>
              {TitleRightMemo}
            </Stack>

            <Box width={"100%"} ref={inputRef}>
              <_PhoneInput
                searchNotFound={translate("not_found")}
                placeholder={props?.placeholder}
                country={translate("localeCountry").toLocaleLowerCase()}
                value={(value as { value: string })?.value}
                onChange={handleOnChange(onChange)}
                onFocus={onFocus}
                onBlur={onBlur}
                isValid={!props.error}
                buttonStyle={{
                  width: props?.size === "small" ? "40px" : "60px",
                  padding:
                    props?.size === "small" ? "0px 0px 0px 0px" : "0 0 0 10px",
                  borderTop: !!error ? "1px solid red" : "",
                  borderLeft: !!error ? "1px solid red" : "",
                  borderBottom: !!error ? "1px solid red" : ""
                }}
                dropdownStyle={{
                  width: dropdownStyle?.width,
                  marginLeft: props?.size === "small" ? "0" : "-10px",
                  padding: "0 10px 10px 10px",
                  borderRadius: 5,
                  maxHeight: "15rem"
                }}
                disableSearchIcon
                searchStyle={{
                  width: "100%",
                  height: props?.size === "small" ? "30px" : "44px",
                  marginLeft: "-5px",
                  marginTop: "10px",
                  borderRadius: 5
                }}
                inputStyle={{
                  ...styles.inputStyle(props?.size),
                  backgroundColor: props?.disabled ? "transparent" : "white",
                  color: props?.disabled ? NAV_PANEL_BG : undefined,
                  border: !!error ? "1px solid red" : ""
                }}
                enableSearch={true}
                disabled={props?.disabled}
                countryCodeEditable={false}
                searchPlaceholder={translate("search")}
              />
            </Box>
            <FormHelperText error={!!error}>
              {error ? error?.message : <></>}
            </FormHelperText>
          </>
        )}
      />
    </Box>
  )
}
